// src/pages/AttendanceOverview/components/modals/ShiftManagementModal.js
import React, { useEffect, useState } from "react";
import {
  Modal,
  Calendar,
  DatePicker,
  TimePicker,
  Input,
  Button,
  Typography,
  Row,
  Col,
  Select,
  App,
} from "antd";
import dayjs from "dayjs";

const { Text } = Typography;

const ShiftManagementModal = ({
  visible,
  onClose,
  onAddShift,
  isDisabled = false,
  allUsers = [],
  weekEndDays = [],
  initialData = null,
}) => {
  const { modal } = App.useApp();
  const [dateTime, setDateTime] = useState(dayjs());
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [checkIn, setCheckIn] = useState(dayjs());
  const [checkOut, setCheckOut] = useState(dayjs());
  const [notes, setNotes] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);

  // ⏰ live date/time
  useEffect(() => {
    const timer = setInterval(() => setDateTime(dayjs()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 🧹 reset fields when modal closes
  useEffect(() => {
    if (!visible) {
      setFromDate(null);
      setToDate(null);
      setCheckIn(dayjs());
      setCheckOut(dayjs());
      setNotes("");
      setSelectedUsers([]);
    }
  }, [visible]);

  useEffect(() => {
    if (initialData?._prefill) {
      const d = initialData._prefill;
      setFromDate(d.from_date);
      setToDate(d.to_date);
      setCheckIn(d.check_in);
      setCheckOut(d.check_out);
      setNotes(d.notes);
      const ids = initialData.emp_ids;
      let users = [];
      if (Array.isArray(ids)) {
        users = ids.map((id) => id);
      } else if (typeof ids === "string") {
        users = ids
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean);
      }
      setSelectedUsers(users);
    } else if (!visible) {
      resetFields();
    }
  }, [initialData, visible]);

  const resetFields = () => {
    setFromDate(null);
    setToDate(null);
    setCheckIn(dayjs());
    setCheckOut(dayjs());
    setNotes("");
    setSelectedUsers([]);
  };

  // 🚫 disable weekends and future dates
  const disableInvalidDates = (current) => {
    if (!current) return false;
    const isWeekend = weekEndDays?.includes(current.day());
    const isFuture = current > dayjs().add(1, "year");
    return isWeekend || isFuture;
  };

  // 🧠 validate + submit
  const handleSave = () => {
    if (!fromDate)
      return modal.warning({
        title: "Select Shift Start Date",
        centered: true,
      });
    if (!toDate)
      return modal.warning({ title: "Select Shift End Date", centered: true });

    const formattedFrom = dayjs(fromDate).format("YYYY-MM-DD");
    const formattedTo = dayjs(toDate).format("YYYY-MM-DD");
    if (formattedFrom > formattedTo)
      return modal.warning({
        title: "The 'From' date cannot be later than the 'To' date.",
        centered: true,
      });

    const checkInTime = checkIn.format("HH:mm:ss");
    const checkOutTime = checkOut.format("HH:mm:ss");

    if (checkInTime === checkOutTime)
      return modal.warning({
        title: "Check-in and Check-out times cannot be the same.",
        centered: true,
      });
    if (checkInTime > checkOutTime)
      return modal.warning({
        title: "Check-in time cannot be later than check-out time.",
        centered: true,
      });

    if (!selectedUsers.length)
      return modal.warning({
        title: "Please select at least one user.",
        centered: true,
      });

    if (!notes.trim())
      return modal.warning({ title: "Notes are required", centered: true });

    onAddShift({
      from_date: formattedFrom,
      to_date: formattedTo,
      check_in: checkInTime,
      check_out: checkOutTime,
      user_ids: selectedUsers,
      notes: notes.trim(),
    });
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      centered
      className="shift-management-modal"
    >
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginTop: "1rem" }}>
        <Col>
          <Text strong className="attendance-modal-title">
            Add New Shift
          </Text>
        </Col>
        <Col>
          <Text className="attendance-modal-time">
            {dateTime.format("DD-MMM-YYYY hh:mm:ss A")}
          </Text>
        </Col>
      </Row>

      {/* Calendar */}
      <div className="attendanceOverview-calendar-container" style={{ marginTop: "2rem" }}>
        <Calendar fullscreen={false} disabledDate={disableInvalidDates} />
      </div>

      {/* Fields */}
      <Row gutter={[16, 16]} style={{ marginTop: "2rem" }}>
        <Col xs={24} sm={12}>
          <label className="form-label">
            Shift Start Date <span className="form-span">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            value={fromDate}
            onChange={(val) => setFromDate(val)}
            format="DD-MMM-YYYY"
            disabledDate={disableInvalidDates}
            placeholder="Select start date"
          />
        </Col>

        <Col xs={24} sm={12}>
          <label className="form-label">
            Shift End Date <span className="form-span">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            value={toDate}
            onChange={(val) => setToDate(val)}
            format="DD-MMM-YYYY"
            disabledDate={disableInvalidDates}
            placeholder="Select end date"
          />
        </Col>

        <Col xs={24} sm={12}>
          <label className="form-label">
            Shift Start Time <span className="form-span">*</span>
          </label>
          <TimePicker
            style={{ width: "100%" }}
            value={checkIn}
            onChange={(val) => setCheckIn(val)}
            format="HH:mm"
            data-testid="shift_start_time"
            inputReadOnly={false}
          />
        </Col>

        <Col xs={24} sm={12}>
          <label className="form-label">
            Shift End Time <span className="form-span">*</span>
          </label>
          <TimePicker
            style={{ width: "100%" }}
            value={checkOut}
            onChange={(val) => setCheckOut(val)}
            format="HH:mm"
            data-testid="shift_end_time"
            inputReadOnly={false}
          />
        </Col>

        <Col xs={24}>
          <label className="form-label">
            Apply Shift To <span className="form-span">*</span>
          </label>
          <Select
            mode="multiple"
            placeholder="Select Employees"
            style={{ width: "100%" }}
            value={selectedUsers}
            onChange={setSelectedUsers}
            options={allUsers.map((u) => ({
              label: u.user_name || u.employee_name,
              value: u.id || u.employee_id,
            }))}
            data-testid="apply_shift_to"
          />
        </Col>

        <Col xs={24}>
          <label className="form-label">
            Notes <span className="form-span">*</span>
          </label>
          <Input
            placeholder="Notes"
            data-testid="notes"
            value={notes}
            onChange={(e) => {
              const val = e.target.value;
              if (val.length <= 50) setNotes(val);
            }}
            onKeyDown={(e) => {
              if (
                notes &&
                notes.length >= 50 &&
                !["Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(
                  e.key
                )
              ) {
                e.preventDefault();
              }
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {notes.length}/50 characters
          </Text>
        </Col>
      </Row>

      {/* Buttons */}
      <div className="attendance-buttons">
        <Button onClick={onClose}>Discard</Button>
        <Button
          type="primary"
          disabled={
            isDisabled ||
            !fromDate ||
            !toDate ||
            !checkIn ||
            !checkOut ||
            !selectedUsers ||
            !notes
          }
          onClick={handleSave}
        >
          Add Shift
        </Button>
      </div>
    </Modal>
  );
};

export default ShiftManagementModal;
